import type { BtnOptions, BtnStyles, CtasObjectModel } from '@Types/Shared/Button';
import type { WysiwygBtnSettings } from '@Types/Shared/Common';
import type { BlockKey } from '@Types/Shared/Options';

export const getBtnSettingsToCSS = (
	ctasOptions: CtasObjectModel | undefined,
	style: BtnStyles,
	context: BlockKey | undefined | 'single-teams-content'
) => {
	const btnOptions = ctasOptions?.[style];

	if (btnOptions?.btnIsBasicLink) {
		return getBasicBtnSettings(style, context, btnOptions);
	} else {
		return getComplexBtnSettings(style, context, btnOptions);
	}
};

export const getArchiveBtnSettingsToCSS = (
	btnOptions: BtnOptions | undefined,
	context: BlockKey | undefined
) => {
	if (btnOptions?.btnIsBasicLink) {
		return getBasicBtnSettings('archivelink', context, btnOptions);
	} else {
		return getComplexBtnSettings('archivelink', context, btnOptions);
	}
};

export const getHomeBtnSettingsToCSS = (
	btnOptions: BtnOptions | undefined,
	context: BlockKey | undefined | '404'
) => {
	if (btnOptions?.btnIsBasicLink) {
		return getBasicBtnSettings('btn', context, btnOptions);
	} else {
		return getComplexBtnSettings('btn', context, btnOptions);
	}
};

export const getReadMoreBtnSettingsToCSS = (
	btnOptions: BtnOptions | undefined,
	context: BlockKey | undefined
) => {
	if (btnOptions?.btnIsBasicLink) {
		return getBasicBtnSettings('readmore', context, btnOptions);
	} else {
		return getComplexBtnSettings('readmore', context, btnOptions);
	}
};

export const getSubmitBtnSettingsToCSS = (
	btnOptions: BtnOptions | undefined,
	context: BlockKey | undefined
) => {
	if (btnOptions?.btnIsBasicLink) {
		return getBasicBtnSettings('submit', context, btnOptions);
	} else {
		return getComplexBtnSettings('submit', context, btnOptions);
	}
};

export const getHeaderBtnSettingsToCSS = (
	btnOptions: BtnOptions | undefined,
	context: BlockKey | 'topNav' | 'mainNav' | undefined
) => {
	if (btnOptions?.btnIsBasicLink) {
		return getBasicBtnSettings('header', context, btnOptions);
	} else {
		return getComplexBtnSettings('header', context, btnOptions);
	}
};

export const getResetBtnSettingsToCSS = (
	btnOptions: BtnOptions | undefined,
	context: BlockKey | undefined | 'search'
) => {
	if (btnOptions?.btnIsBasicLink) {
		return getBasicListingBtnSettings('reset', context, btnOptions);
	} else {
		return getComplexListingBtnSettings('reset', context, btnOptions);
	}
};

const getComplexBtnSettings = (
	style: BtnStyles,
	context?: BlockKey | 'topNav' | 'mainNav' | '404' | 'single-teams-content',
	btnOptions?: BtnOptions
) => {
	const settings: WysiwygBtnSettings = {
		style: style,
		btnHasIcon: btnOptions?.btnHasIcon ? btnOptions?.btnHasIcon : false,
		btnDecorationElement: 'underline',
		ClassName: `minos-${context}-btn-${style} desktop:minos-${context}-btn-${style} tablet:minos-${context}-btn-${style} ${btnOptions?.btnRemovePadding ? 'p-0' : 'px-4 py-2 tablet:px-8 tablet:py-3'}`,
		animation: btnOptions?.animation ? btnOptions.animation : 'none',
		btnStyle: btnOptions,
	};
	return settings;
};

const getBasicBtnSettings = (
	style: BtnStyles,
	context?: BlockKey | 'topNav' | 'mainNav' | '404' | 'single-teams-content',
	btnOptions?: BtnOptions
) => {
	const settings: WysiwygBtnSettings = {
		style: style,
		btnHasIcon: btnOptions?.hasUnderline ? btnOptions?.hasUnderline : false,
		btnDecorationElement: 'underline',
		ClassName: `minos-${context}-btn-${style}-basic tablet:minos-${context}-btn-${style}-basic`,
		btnStyle: btnOptions,
	};

	return settings;
};

const getComplexListingBtnSettings = (
	style: BtnStyles,
	context?: BlockKey | 'search',
	btnOptions?: BtnOptions
) => {
	let className: string = '';
	if (context != 'search') {
		className = `minos-listing-${context}-${style}-btn tablet:minos-listing-${context}-${style}-btn ${btnOptions?.btnRemovePadding ? 'p-0' : 'px-4 py-2 tablet:px-8 tablet:py-3'}`;
	} else {
		className = `minos-${context}-${style}-btn tablet:minos-${context}-${style}-btn ${btnOptions?.btnRemovePadding ? 'p-0' : 'px-4 py-2 tablet:px-8 tablet:py-3'}`;
	}

	const settings: WysiwygBtnSettings = {
		style: style,
		btnHasIcon: btnOptions?.btnHasIcon ? btnOptions?.btnHasIcon : false,
		btnDecorationElement: 'underline',
		ClassName: className,
		animation: btnOptions?.animation ? btnOptions.animation : 'none',
		btnStyle: btnOptions,
	};
	return settings;
};

const getBasicListingBtnSettings = (
	style: BtnStyles,
	context?: BlockKey | 'search',
	btnOptions?: BtnOptions
) => {
	let className: string = '';
	if (context != 'search') {
		className = `minos-listing-${context}-${style}-btn-basic tablet:minos-listing-${context}-${style}-btn-basic px-3 py-2 tablet:px-6 tablet:py-4`;
	} else {
		className = `minos-${context}-${style}-btn-basic tablet:minos-${context}-${style}-btn-basic px-3 py-2 tablet:px-6 tablet:py-4`;
	}

	const settings: WysiwygBtnSettings = {
		style: style,
		btnHasIcon: btnOptions?.btnHasIcon ? btnOptions?.btnHasIcon : false,
		btnDecorationElement: 'underline',
		ClassName: className,
		btnStyle: btnOptions,
	};
	return settings;
};
